/**
 * AI Perfect Information Module
 * Provides full analysis of opponent's visible state for strategic decision-making
 */

import { CardType, GameState, Player, PreparedCard, CardColor, Card, GamePhase } from '../types'
import { getEffectiveEssenceCostForPlayer, isImmuneToDamage } from './gameLogic'

// Logging disabled to improve FPS
const PERFECT_INFO_DEBUG = false
const log = (message: string) => {
    if (PERFECT_INFO_DEBUG) console.log(`[PerfectInfo] ${message}`)
}

export interface OpponentAnalysis {
    // Cards
    handCardTypes: Set<CardType>;
    handSubTypes: Set<'Armory' | 'Spell' | 'Artifact' | undefined>;
    preparedCardTypes: Set<CardType>;
    preparedSubTypes: Set<'Armory' | 'Spell' | 'Artifact' | undefined>;

    // Threats
    maxIncomingDamage: number;
    hasLethalThreat: boolean;
    canCounterAttack: boolean;      // Has Defense, Invincible, Hibernation, etc.
    canCounterSpell: boolean;       // Has Dispel
    canCounterArtifact: boolean;    // Has PsyBlock
    canCounterArmory: boolean;      // Has Pushback
    hasCounterblast: boolean;
    hasInvincible: boolean;

    // Hand counter awareness (including typeB)
    handCanCounterAttack: boolean;
    handCanCounterSpell: boolean;
    handCanCounterArtifact: boolean;
    handCanCounterArmory: boolean;
    handHasHibernation: boolean;
    handHasInvincible: boolean;

    // Resources
    essence: number;
    affordableStashCards: number;   // Stash cards they can activate

    // Strategy prediction
    likelyAttackTurn: boolean;
    likelyDefendTurn: boolean;
    towersThreat: boolean;          // Close to 5 towers
}

export const getCardEffect = (preparedCard: PreparedCard) => {
    const { card, rotation } = preparedCard
    const finalRotation = (rotation % 360 + 360) % 360
    const isFlipped = finalRotation > 90 && finalRotation < 270
    const type = isFlipped && card.typeB ? card.typeB : card.type
    const value = isFlipped && card.valueB ? card.valueB : card.value
    return { type, value }
}

/**
 * Analyze opponent's complete visible state
 */
export function analyzeOpponent(state: GameState, aiId: number): OpponentAnalysis {
    const opponent = state.players.find(p => p.id !== aiId)!
    const ai = state.players.find(p => p.id === aiId)!

    // Hand analysis
    const handCardTypes = new Set<CardType>()
    const handSubTypes = new Set<'Armory' | 'Spell' | 'Artifact' | undefined>()
    opponent.hand.forEach(c => {
        handCardTypes.add(c.type)
        if (c.typeB) handCardTypes.add(c.typeB)
        handSubTypes.add(c.subType as any)
    })

    // Prepared analysis
    const preparedCardTypes = new Set<CardType>()
    const preparedSubTypes = new Set<'Armory' | 'Spell' | 'Artifact' | undefined>()
    opponent.prepared.forEach(pc => {
        const effect = getCardEffect(pc)
        preparedCardTypes.add(effect.type)
        preparedSubTypes.add(pc.card.subType as any)
    })

    // Calculate max incoming damage
    const damageTypes = [
        CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
        CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
        CardType.Chromabow, CardType.Trembling
    ]

    const aiIsImmune = isImmuneToDamage(ai, state.round)
    const cursedIronReduction = ai.effectCards
        .filter(c => c.type === CardType.CursedIron)
        .reduce((acc, c) => acc + (c.value || 0), 0)

    let maxIncomingDamage = 0
    if (!aiIsImmune) {
        opponent.prepared.forEach(pc => {
            const effect = getCardEffect(pc)
            if (damageTypes.includes(effect.type)) {
                const baseDamage = effect.value || 0
                const adjustedDamage = Math.max(0, baseDamage - cursedIronReduction)
                const finalDamage = Math.max(0, adjustedDamage - ai.shield)
                maxIncomingDamage = Math.max(maxIncomingDamage, finalDamage)
            }
        })
    }

    // Counter capabilities
    const canCounterAttack = preparedCardTypes.has(CardType.Defense) ||
        preparedCardTypes.has(CardType.Invincible) ||
        preparedCardTypes.has(CardType.Hibernation) ||
        preparedCardTypes.has(CardType.Pushback) ||
        preparedCardTypes.has(CardType.Counterblast) ||
        preparedCardTypes.has(CardType.Protector)

    const canCounterSpell = preparedCardTypes.has(CardType.Dispel) ||
        preparedCardTypes.has(CardType.SealOfSilence)
    const canCounterArtifact = preparedCardTypes.has(CardType.PsyBlock)
    const canCounterArmory = preparedCardTypes.has(CardType.Pushback)
    const hasCounterblast = preparedCardTypes.has(CardType.Counterblast)
    const hasInvincible = preparedCardTypes.has(CardType.Invincible)

    // Hand counter analysis (including typeB)
    const handHasPushback = opponent.hand.some(c =>
        c.type === CardType.Pushback || c.typeB === CardType.Pushback
    )
    const handHasDefense = opponent.hand.some(c =>
        c.type === CardType.Defense || c.typeB === CardType.Defense
    )
    const handHasDispel = opponent.hand.some(c =>
        c.type === CardType.Dispel || c.typeB === CardType.Dispel || c.type === CardType.SealOfSilence || c.typeB === CardType.SealOfSilence
    )
    const handHasPsyBlock = opponent.hand.some(c =>
        c.type === CardType.PsyBlock || c.typeB === CardType.PsyBlock
    )
    const handHasInvincible = opponent.hand.some(c =>
        c.type === CardType.Invincible || c.typeB === CardType.Invincible
    )
    const handHasHibernation = opponent.hand.some(c =>
        c.type === CardType.Hibernation || c.typeB === CardType.Hibernation
    )
    const handHasCounterblast = opponent.hand.some(c =>
        c.type === CardType.Counterblast || c.typeB === CardType.Counterblast
    )
    const handHasProtector = opponent.hand.some(c =>
        c.type === CardType.Protector || c.typeB === CardType.Protector
    )

    const handCanCounterAttack = handHasDefense || handHasInvincible || handHasHibernation ||
        handHasPushback || handHasCounterblast || handHasProtector
    const handCanCounterSpell = handHasDispel
    const handCanCounterArtifact = handHasPsyBlock
    const handCanCounterArmory = handHasPushback

    // Affordable stash cards
    const affordableStashCards = opponent.prepared.filter(pc => {
        if (!pc.card.essenceCost) return false
        const cost = getEffectiveEssenceCostForPlayer(pc.card, opponent)
        return opponent.essence >= cost
    }).length

    // Strategy prediction
    const attackingCards = opponent.prepared.filter(pc =>
        damageTypes.includes(getCardEffect(pc).type)
    ).length
    const defensiveCards = opponent.prepared.filter(pc => {
        const effect = getCardEffect(pc)
        return [CardType.Defense, CardType.Hibernation, CardType.Invincible].includes(effect.type)
    }).length

    const likelyAttackTurn = attackingCards >= 2 || (attackingCards > defensiveCards)
    const likelyDefendTurn = defensiveCards >= 2 || (defensiveCards > attackingCards)
    const towersThreat = opponent.towers.length >= 4

    const analysis: OpponentAnalysis = {
        handCardTypes,
        handSubTypes,
        preparedCardTypes,
        preparedSubTypes,
        maxIncomingDamage,
        hasLethalThreat: maxIncomingDamage >= ai.health,
        canCounterAttack,
        canCounterSpell,
        canCounterArtifact,
        canCounterArmory,
        hasCounterblast,
        hasInvincible,
        handCanCounterAttack,
        handCanCounterSpell,
        handCanCounterArtifact,
        handCanCounterArmory,
        handHasHibernation,
        handHasInvincible,
        essence: opponent.essence,
        affordableStashCards,
        likelyAttackTurn,
        likelyDefendTurn,
        towersThreat
    }

    log(`Analysis: maxDmg=${maxIncomingDamage}, lethal=${analysis.hasLethalThreat}, canCounter=${canCounterAttack}`)

    return analysis
}

/**
 * Check if opponent can counter a specific card type
 */
export function canOpponentCounter(
    analysis: OpponentAnalysis,
    cardType: CardType,
    subType?: 'Armory' | 'Spell' | 'Artifact'
): boolean {
    // Universal counters
    if (analysis.hasInvincible) {
        const damageTypes = [
            CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
            CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
            CardType.Chromabow, CardType.Trembling, CardType.Disease
        ]
        if (damageTypes.includes(cardType)) return true
    }

    // SubType-based counters
    if (subType === 'Armory' && analysis.canCounterArmory) return true
    if (subType === 'Spell' && analysis.canCounterSpell) return true
    if (subType === 'Artifact' && analysis.canCounterArtifact) return true

    // Type-based counters
    const damageTypes = [
        CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
        CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
        CardType.Chromabow
    ]
    if (damageTypes.includes(cardType) && analysis.canCounterAttack) return true

    return false
}

/**
 * Determine optimal attack window
 */
export function getOptimalAttackWindow(
    analysis: OpponentAnalysis,
    aiHealth: number,
    aiMaxHealth: number
): 'NOW' | 'WAIT' | 'NEVER' {
    // Never attack if opponent has uncounterable Invincible
    if (analysis.hasInvincible) return 'NEVER'

    // Attack now if opponent has no counters
    if (!analysis.canCounterAttack) return 'NOW'

    // Attack now if opponent is low HP and we can finish
    if (analysis.hasLethalThreat) return 'WAIT' // Defend first

    // Attack now in late game or if we're healthy
    const healthRatio = aiHealth / aiMaxHealth
    if (healthRatio > 0.6 && !analysis.hasCounterblast) return 'NOW'

    return 'WAIT'
}

/**
 * Handle Information Masking (AI Privacy)
 * Returns a version of the state where a specific player's hidden information is removed
 */
export function getMaskedState(state: GameState, viewingPlayerId: number): GameState {
    const maskedState = JSON.parse(JSON.stringify(state))
    maskedState.players.forEach((p: Player) => {
        if (p.id !== viewingPlayerId) {
            // Mask the other player's prepared cards if we are in Preparation phase
            // (Standard rule: can't see opponent's prepared cards until combat)
            if (state.phase === GamePhase.Preparation) {
                p.prepared = p.prepared.map(pc => ({
                    ...pc,
                    card: { ...pc.card, faceSlug: 'card_back', type: 'Hidden' as any, value: 0 }
                }))
            }
            // Optional: Mask hand if not "Perfect Info" mode
            // p.hand = p.hand.map(c => ({ id: c.id, faceSlug: 'card_back', type: 'Hidden' as any }))
        }
    })
    return maskedState
}
